import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/kiteApi';
import { bumpSymbolCatalogVersion, importSymbolsCsv } from '../api/symbolsApi';
import { buildTargetPrices, formatRatio } from '../utils/targetCalculator';
import {
  allocateTargetQuantities,
  normalizeTradeConfig,
  saveTradeConfig,
  STOPLOSS_MOVEMENT_ACTIONS,
} from '../utils/tradeConfig';
import {
  buildRiskPresetRange,
  loadTradingPreferences,
  RISK_PRESET_OPTIONS,
  saveTradingPreferences as persistTradingPreferences,
} from '../utils/tradingPreferences';

export default function TradeConfigCard({ config, onChanged }) {
  const initialPreferences = useMemo(() => loadTradingPreferences(), []);
  const [draft, setDraft] = useState(config);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [activeConfigTab, setActiveConfigTab] = useState('general');
  const [symbolFile, setSymbolFile] = useState(null);
  const [symbolFileName, setSymbolFileName] = useState('');
  const [symbolImportSummary, setSymbolImportSummary] = useState(null);
  const [symbolImportLoading, setSymbolImportLoading] = useState(false);
  const [telegramConfig, setTelegramConfig] = useState({
    telegram_enabled: false,
    bot_token: '',
    channel_chat_id: '',
    message_thread_id: '',
    send_target_hits: true,
    send_stoploss_hits: true,
    has_bot_token: false,
    bot_token_masked: '',
  });
  const [telegramLoading, setTelegramLoading] = useState(false);
  const [telegramTesting, setTelegramTesting] = useState(false);
  const [telegramTestResult, setTelegramTestResult] = useState(null);
  const [monitorConfig, setMonitorConfig] = useState({
    live_trade_poll_ms: 5000,
  });
  const [monitorLoading, setMonitorLoading] = useState(false);
  const [liveExecutionConfig, setLiveExecutionConfig] = useState({
    telegram_enabled: false,
    bot_token: '',
    channel_chat_id: '',
    message_thread_id: '',
    send_target_hits: true,
    send_stoploss_hits: true,
    send_errors: true,
    send_order_results: true,
    has_bot_token: false,
    bot_token_masked: '',
    live_execution_poll_ms: 5000,
    market_execution_guard_enabled: true,
    market_open_time: '09:15',
    market_close_time: '15:30',
    market_timezone: 'Asia/Kolkata',
    gtt_sl_limit_buffer_percent: 1,
    gtt_order_chase_enabled: true,
    gtt_order_chase_after_ms: 3000,
    gtt_order_chase_step_percent: 1,
    gtt_order_chase_max_attempts: 2,
    gtt_order_chase_to_market: false,
    gtt_target_order_chase_enabled: false,
  });
  const [liveExecutionLoading, setLiveExecutionLoading] = useState(false);
  const [liveExecutionTesting, setLiveExecutionTesting] = useState(false);
  const [liveExecutionMonitorLoading, setLiveExecutionMonitorLoading] = useState(false);
  const [cleanupConfig, setCleanupConfig] = useState({
    stale_cleanup_enabled: false,
    stale_cleanup_grace_minutes: 5,
    delivery_plan_migration_enabled: true,
  });
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [realRiskLimitPercentConfig, setRealRiskLimitPercentConfig] = useState(initialPreferences.realRiskLimitPercentConfig);
  const [defaultRiskPresetConfig, setDefaultRiskPresetConfig] = useState(initialPreferences.defaultRiskPresetConfig);
  const [riskPresetRangeStart, setRiskPresetRangeStart] = useState(initialPreferences.riskPresetRangeStart);
  const [riskPresetRangeEnd, setRiskPresetRangeEnd] = useState(initialPreferences.riskPresetRangeEnd);
  const [realWalletOverrideInput, setRealWalletOverrideInput] = useState(initialPreferences.realWalletOverrideInput);
  const configuredRiskPresetOptions = buildRiskPresetRange(riskPresetRangeStart, riskPresetRangeEnd);

  useEffect(() => {
    if (!configuredRiskPresetOptions.includes(defaultRiskPresetConfig)) {
      setDefaultRiskPresetConfig(configuredRiskPresetOptions[0]);
    }
  }, [configuredRiskPresetOptions, defaultRiskPresetConfig]);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [telegramResponse, monitorResponse, liveExecutionResponse, liveExecutionMonitorResponse, cleanupResponse] = await Promise.all([
          api.get('/notifications/telegram'),
          api.get('/notifications/monitor'),
          api.get('/live-execution/config'),
          api.get('/live-execution/monitor'),
          api.get('/notifications/cleanup'),
        ]);
        if (!active) return;
        setTelegramConfig((current) => ({
          ...current,
          ...telegramResponse.data,
          bot_token: '',
        }));
        setMonitorConfig({
          live_trade_poll_ms: monitorResponse.data?.live_trade_poll_ms || 5000,
        });
        setLiveExecutionConfig((current) => ({
          ...current,
          ...liveExecutionResponse.data,
          bot_token: '',
          live_execution_poll_ms: liveExecutionMonitorResponse.data?.live_execution_poll_ms || 5000,
        }));
        setCleanupConfig({
          stale_cleanup_enabled: Boolean(cleanupResponse.data?.stale_cleanup_enabled),
          stale_cleanup_grace_minutes: cleanupResponse.data?.stale_cleanup_grace_minutes || 5,
          delivery_plan_migration_enabled: cleanupResponse.data?.delivery_plan_migration_enabled !== false,
        });
      } catch (requestError) {
        if (!active) return;
        setError(requestError.response?.data?.error || requestError.message || 'Unable to load global configuration.');
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const normalizedDraft = normalizeTradeConfig(draft);
  const exampleQuantities = allocateTargetQuantities(20, normalizedDraft.target_percentages);
  const totalPercentage = normalizedDraft.target_percentages.reduce((sum, value) => sum + Number(value), 0);
  const exampleAllocated = exampleQuantities.reduce((sum, value) => sum + value, 0);
  const ladderPreview = useMemo(
    () => buildTargetPrices(100, 95, 'BUY', Array.from({ length: normalizedDraft.target_count }, (_, index) => index + 1)),
    [normalizedDraft.target_count],
  );
  const stoplossMovementPlanCount = Math.max(draft.target_count, ladderPreview.length || 0);
  const normalizedDraftWithLadderPlan = useMemo(
    () => normalizeTradeConfig({ ...draft, stoploss_movement_plan_count: stoplossMovementPlanCount }),
    [draft, stoplossMovementPlanCount],
  );
  const stoplossPlanLabels = useMemo(
    () => Array.from({ length: stoplossMovementPlanCount }, (_, index) => {
      const previewTarget = ladderPreview[index];
      return previewTarget ? `After ${formatRatio(previewTarget.ratio)}` : `After target ${index + 1}`;
    }),
    [stoplossMovementPlanCount, ladderPreview],
  );
  const earlierPercentageTotal = draft.target_percentages
    .slice(0, Math.max(0, draft.target_count - 1))
    .reduce((sum, value) => sum + (Number(value) || 0), 0);
  const hasAllocationError = earlierPercentageTotal >= 100;

  const changeTargetCount = (value) => {
    setMessage(null);
    setError(null);
    setDraft((current) => normalizeTradeConfig({ ...current, target_count: value }));
  };

  const changePercentage = (index, value) => {
    setMessage(null);
    setError(null);
    setDraft((current) => {
      const targetPercentages = [...current.target_percentages];
      targetPercentages[index] = value;
      return { ...current, target_percentages: targetPercentages };
    });
  };

  const changeStoplossMovement = (index, value) => {
    setMessage(null);
    setError(null);
    setDraft((current) => {
      const nextPlan = [...(current.stoploss_movement_plan || [])];
      nextPlan[index] = value;
      return normalizeTradeConfig({
        ...current,
        stoploss_movement_plan_count: stoplossMovementPlanCount,
        stoploss_movement_plan: nextPlan,
      });
    });
  };

  const saveTargetAllocation = () => {
    if (hasAllocationError) {
      setMessage(null);
      setError('Earlier target percentages must total less than 100%. Reduce one or more values.');
      return;
    }
    const normalized = saveTradeConfig({ ...draft, stoploss_movement_plan_count: stoplossMovementPlanCount });
    setDraft(normalized);
    setError(null);
    setMessage('Global target plan saved locally.');
    if (typeof onChanged === 'function') onChanged(normalized);
  };

  const handleSaveTradingPreferences = () => {
    const walletOverrideValue = Number(realWalletOverrideInput);
    if (realWalletOverrideInput && !Number.isFinite(walletOverrideValue)) {
      setMessage(null);
      setError('Real wallet override must be a valid number.');
      return;
    }

    persistTradingPreferences({
      realRiskLimitPercentConfig,
      defaultRiskPresetConfig,
      riskPresetRangeStart,
      riskPresetRangeEnd,
      realWalletOverrideInput,
    });
    setError(null);
    setMessage('Trading preferences saved locally.');
  };

  const selectSymbolFile = (event) => {
    const file = event.target.files?.[0];
    setMessage(null);
    setError(null);
    setSymbolImportSummary(null);
    setSymbolFile(file || null);
    setSymbolFileName(file?.name || '');
  };

  const submitSymbolFile = async () => {
    if (!symbolFile) {
      setMessage(null);
      setError('Choose a symbol file before submitting.');
      return;
    }

    setMessage(null);
    setError(null);
    setSymbolImportSummary(null);
    setSymbolImportLoading(true);

    try {
      const csvText = await symbolFile.text();
      const result = await importSymbolsCsv(csvText);
      bumpSymbolCatalogVersion();
      setSymbolImportSummary(result);
      setMessage(
        result.new_symbols_added > 0
          ? `Uploaded successfully. ${result.new_symbols_added} new stock symbols added from ${symbolFile.name}.`
          : `Uploaded successfully. No new stock symbols were added from ${symbolFile.name}.`,
      );
      setSymbolFile(null);
      setSymbolFileName('');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to import symbol CSV.');
    } finally {
      setSymbolImportLoading(false);
    }
  };

  const updateTelegramConfig = (changes) => {
    setMessage(null);
    setError(null);
    setTelegramConfig((current) => ({ ...current, ...changes }));
  };

  const updateLiveExecutionConfig = (changes) => {
    setMessage(null);
    setError(null);
    setLiveExecutionConfig((current) => ({ ...current, ...changes }));
  };

  const saveTelegramConfig = async () => {
    setMessage(null);
    setError(null);
    setTelegramLoading(true);
    try {
      const response = await api.put('/notifications/telegram', {
        telegram_enabled: telegramConfig.telegram_enabled,
        bot_token: telegramConfig.bot_token,
        channel_chat_id: telegramConfig.channel_chat_id,
        message_thread_id: telegramConfig.message_thread_id,
        send_target_hits: telegramConfig.send_target_hits,
        send_stoploss_hits: telegramConfig.send_stoploss_hits,
      });
      setTelegramConfig((current) => ({
        ...current,
        ...(response.data?.config || {}),
        bot_token: '',
      }));
      setMessage('Telegram notification settings saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to save Telegram notification settings.');
    } finally {
      setTelegramLoading(false);
    }
  };

  const sendTelegramTest = async () => {
    setMessage(null);
    setError(null);
    setTelegramTestResult(null);
    setTelegramTesting(true);
    try {
      const response = await api.post('/notifications/telegram/test', {
        telegram_enabled: telegramConfig.telegram_enabled,
        ...(telegramConfig.bot_token ? { bot_token: telegramConfig.bot_token } : {}),
        channel_chat_id: telegramConfig.channel_chat_id,
        message_thread_id: telegramConfig.message_thread_id,
      });
      const resultMessage = response.data?.message || 'Test notification sent successfully.';
      setMessage(resultMessage);
      setTelegramTestResult({ ok: true, text: resultMessage });
    } catch (requestError) {
      const errorMessage = requestError.response?.data?.error || requestError.message || 'Unable to send test notification.';
      setError(errorMessage);
      setTelegramTestResult({ ok: false, text: errorMessage });
    } finally {
      setTelegramTesting(false);
    }
  };

  const saveLiveExecutionConfig = async () => {
    const timePattern = /^\d{2}:\d{2}$/;
    const chaseNumbers = {
      gtt_sl_limit_buffer_percent: Number(liveExecutionConfig.gtt_sl_limit_buffer_percent),
      gtt_order_chase_after_ms: Number(liveExecutionConfig.gtt_order_chase_after_ms),
      gtt_order_chase_step_percent: Number(liveExecutionConfig.gtt_order_chase_step_percent),
      gtt_order_chase_max_attempts: Number(liveExecutionConfig.gtt_order_chase_max_attempts),
    };
    setMessage(null);
    setError(null);
    if (Object.values(chaseNumbers).some((value) => !Number.isFinite(value) || value < 0)) {
      setError('GTT chase settings must be zero or positive numbers.');
      return;
    }
    if (!timePattern.test(String(liveExecutionConfig.market_open_time || '')) || !timePattern.test(String(liveExecutionConfig.market_close_time || ''))) {
      setError('Market open and close time must be in HH:MM format.');
      return;
    }
    if (!String(liveExecutionConfig.market_timezone || '').trim()) {
      setError('Market timezone is required.');
      return;
    }
    setLiveExecutionLoading(true);
    try {
      const response = await api.put('/live-execution/config', {
        telegram_enabled: liveExecutionConfig.telegram_enabled,
        bot_token: liveExecutionConfig.bot_token,
        channel_chat_id: liveExecutionConfig.channel_chat_id,
        message_thread_id: liveExecutionConfig.message_thread_id,
        send_target_hits: liveExecutionConfig.send_target_hits,
        send_stoploss_hits: liveExecutionConfig.send_stoploss_hits,
        send_errors: liveExecutionConfig.send_errors,
        send_order_results: liveExecutionConfig.send_order_results,
        market_execution_guard_enabled: Boolean(liveExecutionConfig.market_execution_guard_enabled),
        market_open_time: liveExecutionConfig.market_open_time,
        market_close_time: liveExecutionConfig.market_close_time,
        market_timezone: String(liveExecutionConfig.market_timezone || '').trim(),
        gtt_sl_limit_buffer_percent: chaseNumbers.gtt_sl_limit_buffer_percent,
        gtt_order_chase_enabled: Boolean(liveExecutionConfig.gtt_order_chase_enabled),
        gtt_order_chase_after_ms: Math.round(chaseNumbers.gtt_order_chase_after_ms),
        gtt_order_chase_step_percent: chaseNumbers.gtt_order_chase_step_percent,
        gtt_order_chase_max_attempts: Math.round(chaseNumbers.gtt_order_chase_max_attempts),
        gtt_order_chase_to_market: Boolean(liveExecutionConfig.gtt_order_chase_to_market),
        gtt_target_order_chase_enabled: Boolean(liveExecutionConfig.gtt_target_order_chase_enabled),
      });
      setLiveExecutionConfig((current) => ({
        ...current,
        ...(response.data?.config || {}),
        bot_token: '',
      }));
      setMessage('Live execution notification settings saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to save live execution notification settings.');
    } finally {
      setLiveExecutionLoading(false);
    }
  };

  const sendLiveExecutionTest = async () => {
    setMessage(null);
    setError(null);
    setTelegramTestResult(null);
    setLiveExecutionTesting(true);
    try {
      const response = await api.post('/live-execution/telegram/test', {
        telegram_enabled: liveExecutionConfig.telegram_enabled,
        ...(liveExecutionConfig.bot_token ? { bot_token: liveExecutionConfig.bot_token } : {}),
        channel_chat_id: liveExecutionConfig.channel_chat_id,
        message_thread_id: liveExecutionConfig.message_thread_id,
      });
      const resultMessage = response.data?.message || 'Live execution test notification sent successfully.';
      setMessage(resultMessage);
      setTelegramTestResult({ ok: true, text: resultMessage });
    } catch (requestError) {
      const errorMessage = requestError.response?.data?.error || requestError.message || 'Unable to send live execution test notification.';
      setError(errorMessage);
      setTelegramTestResult({ ok: false, text: errorMessage });
    } finally {
      setLiveExecutionTesting(false);
    }
  };

  const saveLiveExecutionMonitorConfig = async () => {
    const nextPollMs = Math.round(Number(liveExecutionConfig.live_execution_poll_ms));
    if (!Number.isFinite(nextPollMs) || nextPollMs < 1000) {
      setMessage(null);
      setError('Live execution monitor check frequency must be at least 1000 ms.');
      return;
    }

    setMessage(null);
    setError(null);
    setLiveExecutionMonitorLoading(true);
    try {
      const response = await api.put('/live-execution/monitor', {
        live_execution_poll_ms: nextPollMs,
      });
      setLiveExecutionConfig((current) => ({
        ...current,
        live_execution_poll_ms: response.data?.live_execution_poll_ms || nextPollMs,
      }));
      setMessage(response.data?.message || 'Live execution monitor frequency saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to save live execution monitor frequency.');
    } finally {
      setLiveExecutionMonitorLoading(false);
    }
  };

  const saveMonitorConfig = async () => {
    const nextPollMs = Math.round(Number(monitorConfig.live_trade_poll_ms));
    if (!Number.isFinite(nextPollMs) || nextPollMs < 1000) {
      setMessage(null);
      setError('Live monitor check frequency must be at least 1000 ms.');
      return;
    }

    setMessage(null);
    setError(null);
    setMonitorLoading(true);
    try {
      const response = await api.put('/notifications/monitor', {
        live_trade_poll_ms: nextPollMs,
      });
      setMonitorConfig({
        live_trade_poll_ms: response.data?.live_trade_poll_ms || nextPollMs,
      });
      setMessage(response.data?.message || 'Live monitor frequency saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to save live monitor frequency.');
    } finally {
      setMonitorLoading(false);
    }
  };

  const saveCleanupConfig = async () => {
    const graceMinutes = Math.round(Number(cleanupConfig.stale_cleanup_grace_minutes));
    if (!Number.isFinite(graceMinutes) || graceMinutes < 1 || graceMinutes > 1440) {
      setMessage(null);
      setError('Cleanup grace period must be from 1 to 1440 minutes.');
      return;
    }

    setMessage(null);
    setError(null);
    setCleanupLoading(true);
    try {
      const response = await api.put('/notifications/cleanup', {
        stale_cleanup_enabled: cleanupConfig.stale_cleanup_enabled,
        stale_cleanup_grace_minutes: graceMinutes,
        delivery_plan_migration_enabled: cleanupConfig.delivery_plan_migration_enabled,
      });
      setCleanupConfig({
        stale_cleanup_enabled: Boolean(response.data?.stale_cleanup_enabled),
        stale_cleanup_grace_minutes: response.data?.stale_cleanup_grace_minutes || graceMinutes,
        delivery_plan_migration_enabled: response.data?.delivery_plan_migration_enabled !== false,
      });
      setMessage(response.data?.message || 'Local cleanup settings saved.');
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || 'Unable to save cleanup settings.');
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="card config-card">
      <div className="card-header">
        <div>
          <div className="section-kicker">Central configuration</div>
          <h2>Trading preferences</h2>
        </div>
      </div>
      {message ? <div className="success-message">{message}</div> : null}
      {error || hasAllocationError ? <div className="error">{error || 'Earlier target percentages must total less than 100%.'}</div> : null}
      <div className="config-tabs" role="tablist" aria-label="Configuration sections">
        {[['general', 'General'], ['telegram', 'Telegram'], ['live', 'Live execution'], ['symbols', 'Symbols']].map(([value, label]) => (
          <button key={value} type="button" role="tab" aria-selected={activeConfigTab === value} className={`config-tab ${activeConfigTab === value ? 'is-active' : ''}`} onClick={() => setActiveConfigTab(value)}>{label}</button>
        ))}
      </div>
      <div className="config-body">
        <div style={{ display: activeConfigTab === 'general' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Global target exit plan</strong>
            <span>Set the default number of targets, quantity percentages, and SL movement used across the app.</span>
          </div>
          <label className="config-count">
            Number of targets
            <select value={draft.target_count} onChange={(event) => changeTargetCount(event.target.value)}>
              {Array.from({ length: 10 }, (_, index) => index + 1).map((count) => <option value={count} key={count}>{count}</option>)}
            </select>
          </label>
          <div className="config-target-grid">
            {Array.from({ length: draft.target_count }, (_, index) => (
              <label key={index}>
                T{index + 1}
                <div><input type="number" min="0" max="100" step="1" disabled={index === draft.target_count - 1} value={index === draft.target_count - 1 ? normalizedDraft.target_percentages[index] : draft.target_percentages[index]} onChange={(event) => changePercentage(index, event.target.value)} /><span>%</span></div>
              </label>
            ))}
          </div>
          <div className="config-example">
            <strong>Example for 20 quantity</strong>
            <span>{normalizedDraft.target_percentages.map((percentage, index) => `T${index + 1}: ${exampleQuantities[index]} qty (${percentage}%)`).join('  /  ')}</span>
            <small>{totalPercentage}% configured, {exampleAllocated} of 20 quantity allocated. Final target is remainder-adjusted. These percentages become the global default for each stock.</small>
          </div>
          <div className="config-section-title" style={{ marginTop: '14px' }}>
            <strong>SL movement plan</strong>
            <span>Set what the stop-loss should move to after each target hit.</span>
          </div>
          <div className="config-target-grid config-target-grid-wide">
            {Array.from({ length: stoplossMovementPlanCount }, (_, index) => (
              <label key={`sl-move-${index}`}>
                {stoplossPlanLabels[index]}
                <div>
                  <select value={normalizedDraftWithLadderPlan.stoploss_movement_plan[index]} onChange={(event) => changeStoplossMovement(index, event.target.value)}>
                    {STOPLOSS_MOVEMENT_ACTIONS.map((action) => (
                      <option key={action.value} value={action.value}>{action.label}</option>
                    ))}
                  </select>
                </div>
              </label>
            ))}
          </div>
          <div className="config-example">
            <strong>SL movement preview</strong>
            <span>{normalizedDraftWithLadderPlan.stoploss_movement_plan.map((action, index) => `${stoplossPlanLabels[index]}: ${STOPLOSS_MOVEMENT_ACTIONS.find((item) => item.value === action)?.label || action}`).join('  /  ')}</span>
            <small>These actions become the default, and each stock can still change them directly in its own card.</small>
          </div>
          <button type="button" className="button button-primary" onClick={saveTargetAllocation}>Save global target plan</button>
        </div>

        <div style={{ display: activeConfigTab === 'live' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Live monitor frequency</strong>
            <span>Choose how often the backend checks active notification and live-execution plans.</span>
          </div>
          <div className="config-target-grid">
            <label>
              Check interval
              <div><input type="number" min="1000" step="500" value={monitorConfig.live_trade_poll_ms} onChange={(event) => setMonitorConfig({ live_trade_poll_ms: event.target.value })} /><span>ms</span></div>
            </label>
          </div>
          <div className="config-example">
            <strong>Current behavior</strong>
            <span>The live monitor checks active plans every {Number(monitorConfig.live_trade_poll_ms) || 5000} ms.</span>
            <small>Lower values react faster but increase broker polling load. 5000 ms is the current default.</small>
          </div>
          <button type="button" className="button button-primary" onClick={saveMonitorConfig} disabled={monitorLoading}>
            {monitorLoading ? 'Saving...' : 'Save monitor frequency'}
          </button>
        </div>

        <div style={{ display: activeConfigTab === 'general' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Local data cleanup</strong>
            <span>Optionally remove saved stock plans after Kite confirms they are no longer held or open.</span>
          </div>
          <div className="config-target-grid">
            <label>
              Delivery plan migration
              <div className="config-toggle">
                <input type="checkbox" checked={cleanupConfig.delivery_plan_migration_enabled} onChange={(event) => setCleanupConfig((current) => ({ ...current, delivery_plan_migration_enabled: event.target.checked }))} />
                <span>{cleanupConfig.delivery_plan_migration_enabled ? 'Move CNC plans to Holdings automatically' : 'Keep Position and Holding plans separate'}</span>
              </div>
            </label>
            <label>
              Automatic cleanup
              <div className="config-toggle">
                <input type="checkbox" checked={cleanupConfig.stale_cleanup_enabled} onChange={(event) => setCleanupConfig((current) => ({ ...current, stale_cleanup_enabled: event.target.checked }))} />
                <span>{cleanupConfig.stale_cleanup_enabled ? 'Enabled during Kite sync' : 'Disabled; keep local plans'}</span>
              </div>
            </label>
            <label>
              Missing grace period
              <div><input type="number" min="1" max="1440" step="1" value={cleanupConfig.stale_cleanup_grace_minutes} onChange={(event) => setCleanupConfig((current) => ({ ...current, stale_cleanup_grace_minutes: event.target.value }))} /><span>min</span></div>
            </label>
          </div>
          <div className="config-example">
            <strong>{cleanupConfig.delivery_plan_migration_enabled ? 'Delivery migration is enabled' : 'Delivery migration is disabled'}</strong>
            <span>{cleanupConfig.delivery_plan_migration_enabled ? 'When Kite shows a CNC stock in Holdings and no longer shows an open CNC Position, its saved SL, targets, and notifications move to the Holding.' : 'Next-day Holdings will not inherit saved plans from CNC Positions.'}</span>
            <small>This migration does not delete the plan; it changes its local type from Position to Holding.</small>
          </div>
          <div className="config-example">
            <strong>{cleanupConfig.stale_cleanup_enabled ? 'Automatic cleanup is enabled' : 'Automatic cleanup is disabled'}</strong>
            <span>{cleanupConfig.stale_cleanup_enabled ? `A missing stock is removed after ${Number(cleanupConfig.stale_cleanup_grace_minutes) || 5} minutes and a later successful Kite sync.` : 'No local plans will be marked or deleted during normal Kite sync.'}</span>
            <small>Kite API errors never remove data. The stock-page manual cleanup button remains available separately.</small>
          </div>
          <button type="button" className="button button-primary" onClick={saveCleanupConfig} disabled={cleanupLoading}>
            {cleanupLoading ? 'Saving...' : 'Save cleanup settings'}
          </button>
        </div>

        <div style={{ display: activeConfigTab === 'general' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Risk and position sizing</strong>
            <span>Choose the preset range and risk caps shown in the position sizing section.</span>
          </div>
          <div className="config-target-grid">
            <label>
              Position sizing start
              <div>
                <select value={riskPresetRangeStart} onChange={(event) => {
                  const nextStart = Number(event.target.value);
                  setRiskPresetRangeStart(nextStart);
                  if (nextStart > riskPresetRangeEnd) setRiskPresetRangeEnd(nextStart);
                }}>
                  {RISK_PRESET_OPTIONS.map((percentage) => <option key={percentage} value={percentage}>{percentage}%</option>)}
                </select>
              </div>
            </label>
            <label>
              Position sizing end
              <div>
                <select value={riskPresetRangeEnd} onChange={(event) => {
                  const nextEnd = Number(event.target.value);
                  setRiskPresetRangeEnd(nextEnd);
                  if (nextEnd < riskPresetRangeStart) setRiskPresetRangeStart(nextEnd);
                }}>
                  {RISK_PRESET_OPTIONS.map((percentage) => <option key={percentage} value={percentage}>{percentage}%</option>)}
                </select>
              </div>
            </label>
            <label>
              Default preset
              <div>
                <select value={defaultRiskPresetConfig} onChange={(event) => setDefaultRiskPresetConfig(Number(event.target.value))}>
                  {configuredRiskPresetOptions.map((percentage) => <option key={percentage} value={percentage}>{percentage}%</option>)}
                </select>
              </div>
            </label>
            <label>
              Real max risk
              <div>
                <select value={realRiskLimitPercentConfig} onChange={(event) => setRealRiskLimitPercentConfig(Number(event.target.value))}>
                  {RISK_PRESET_OPTIONS.map((percentage) => <option key={percentage} value={percentage}>{percentage}%</option>)}
                </select>
              </div>
            </label>
          </div>
          <div className="config-example">
            <strong>Position sizing preview</strong>
            <span>{configuredRiskPresetOptions.map((percentage) => `${percentage}%`).join('  /  ')}</span>
            <small>The order page will use this start-to-end range, and Max available will choose the highest preset within the remaining risk capacity.</small>
          </div>
          <button type="button" className="button button-primary" onClick={handleSaveTradingPreferences}>Save real trading settings</button>
        </div>

        <div style={{ display: activeConfigTab === 'telegram' || activeConfigTab === 'live' ? 'block' : 'none' }}>
          <div style={{ display: activeConfigTab === 'telegram' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Telegram configuration</strong>
            <span>Configure one Telegram bot and chat destination for all dashboard, entry, target, stop-loss, and live-order alerts.</span>
          </div>
          <div className="config-target-grid">
            <label>
              Alerts enabled
              <div className="config-toggle">
                <input type="checkbox" checked={telegramConfig.telegram_enabled} onChange={(event) => updateTelegramConfig({ telegram_enabled: event.target.checked })} />
                <span>Send automation alerts to Telegram</span>
              </div>
            </label>
            <label>
              Bot token
              <div><input type="password" value={telegramConfig.bot_token} onChange={(event) => updateTelegramConfig({ bot_token: event.target.value })} placeholder={telegramConfig.has_bot_token ? `Saved: ${telegramConfig.bot_token_masked}` : '123456:ABC-DEF...'} /></div>
            </label>
            <label>
              Channel username or chat id
              <div><input type="text" value={telegramConfig.channel_chat_id} onChange={(event) => updateTelegramConfig({ channel_chat_id: event.target.value })} placeholder="@your_channel or -100..." /></div>
            </label>
            <label>
              Topic / thread id
              <div><input type="text" value={telegramConfig.message_thread_id} onChange={(event) => updateTelegramConfig({ message_thread_id: event.target.value })} placeholder="Optional: forum topic id" /></div>
            </label>
          </div>
          <div className="config-target-grid">
            <label>
              Target hits
              <div className="config-toggle">
                <input type="checkbox" checked={telegramConfig.send_target_hits} onChange={(event) => updateTelegramConfig({ send_target_hits: event.target.checked })} />
                <span>Send message when a target exits</span>
              </div>
            </label>
            <label>
              Stop-loss hits
              <div className="config-toggle">
                <input type="checkbox" checked={telegramConfig.send_stoploss_hits} onChange={(event) => updateTelegramConfig({ send_stoploss_hits: event.target.checked })} />
                <span>Send message when a stop-loss exits</span>
              </div>
            </label>
          </div>
          <div className="config-example">
            <strong>Telegram setup notes</strong>
            <span>Leave Bot token blank while saving if you want to keep the current stored token. The channel can be a public `@username` or a numeric Telegram chat id. If you post into a Telegram forum topic, enter the topic/thread id too.</span>
          </div>
          <div className="config-button-row">
            <button type="button" className="button button-primary" onClick={saveTelegramConfig} disabled={telegramLoading}>
              {telegramLoading ? 'Saving...' : 'Save Telegram settings'}
            </button>
            <button type="button" className="button button-secondary" onClick={sendTelegramTest} disabled={telegramTesting}>
              {telegramTesting ? 'Sending...' : 'Send test notification'}
            </button>
          </div>
          {telegramTestResult ? <div className={`config-example telegram-test-result ${telegramTestResult.ok ? 'is-success' : 'is-error'}`}><strong>{telegramTestResult.ok ? '✓ Telegram notification sent' : '✕ Telegram notification failed'}</strong><span>{telegramTestResult.text}</span></div> : null}
        </div>

        </div>

        <div style={{ display: activeConfigTab === 'telegram' || activeConfigTab === 'live' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>Telegram alert controls</strong>
            <span>These controls use the Telegram configuration above. Live execution also falls back to those shared credentials.</span>
          </div>
          <div className="config-target-grid">
            <label>
              Alerts enabled
              <div className="config-toggle">
                <input type="checkbox" checked={liveExecutionConfig.telegram_enabled} onChange={(event) => updateLiveExecutionConfig({ telegram_enabled: event.target.checked })} />
                <span>Send live execution alerts to Telegram</span>
              </div>
            </label>
          </div>
          <div className="config-target-grid">
            <label>
              Target hits
              <div className="config-toggle">
                <input type="checkbox" checked={liveExecutionConfig.send_target_hits} onChange={(event) => updateLiveExecutionConfig({ send_target_hits: event.target.checked })} />
                <span>Send message when a target exit happens</span>
              </div>
            </label>
            <label>
              Stop-loss hits
              <div className="config-toggle">
                <input type="checkbox" checked={liveExecutionConfig.send_stoploss_hits} onChange={(event) => updateLiveExecutionConfig({ send_stoploss_hits: event.target.checked })} />
                <span>Send message when a stop-loss exit happens</span>
              </div>
            </label>
            <label>
              Kite / execution errors
              <div className="config-toggle">
                <input type="checkbox" checked={liveExecutionConfig.send_errors} onChange={(event) => updateLiveExecutionConfig({ send_errors: event.target.checked })} />
                <span>Send message when order placement or Kite fetch fails</span>
              </div>
            </label>
            <label>
              Live order results
              <div className="config-toggle">
                <input type="checkbox" checked={liveExecutionConfig.send_order_results} onChange={(event) => updateLiveExecutionConfig({ send_order_results: event.target.checked })} />
                <span>Send success and failure messages for market orders</span>
              </div>
            </label>
          </div>
          <div style={{ display: activeConfigTab === 'live' ? 'block' : 'none' }}>
          <div className="config-example">
            <strong>Live execution behavior</strong>
            <span>The separate live execution monitor checks enabled plans every {Number(liveExecutionConfig.live_execution_poll_ms) || 5000} ms and can alert on target fills, stop-loss exits, and Kite errors.</span>
            <small>This configuration is completely separate from the current notification settings above.</small>
          </div>
          <div className="config-section-title" style={{ marginTop: '14px' }}>
            <strong>Market hours guard</strong>
            <span>Pause automatic execution and GTT mutation outside your configured trading session.</span>
          </div>
          <div className="config-target-grid config-target-grid-wide">
            <label>
              Execution guard
              <div className="config-toggle">
                <input type="checkbox" checked={Boolean(liveExecutionConfig.market_execution_guard_enabled)} onChange={(event) => updateLiveExecutionConfig({ market_execution_guard_enabled: event.target.checked })} />
                <span>{liveExecutionConfig.market_execution_guard_enabled ? 'Pause automation when market is closed' : 'Allow automation at any time'}</span>
              </div>
            </label>
            <label>
              Market open
              <div><input type="time" value={liveExecutionConfig.market_open_time} onChange={(event) => updateLiveExecutionConfig({ market_open_time: event.target.value })} /></div>
            </label>
            <label>
              Market close
              <div><input type="time" value={liveExecutionConfig.market_close_time} onChange={(event) => updateLiveExecutionConfig({ market_close_time: event.target.value })} /></div>
            </label>
            <label>
              Timezone
              <div><input type="text" value={liveExecutionConfig.market_timezone} onChange={(event) => updateLiveExecutionConfig({ market_timezone: event.target.value })} placeholder="Asia/Kolkata" /></div>
            </label>
          </div>
          <div className="config-example">
            <strong>{liveExecutionConfig.market_execution_guard_enabled ? 'Automatic execution pauses outside session' : 'Automatic execution always allowed'}</strong>
            <span>{liveExecutionConfig.market_execution_guard_enabled ? `The monitors will switch to paused mode outside ${liveExecutionConfig.market_open_time || '09:15'} to ${liveExecutionConfig.market_close_time || '15:30'} (${liveExecutionConfig.market_timezone || 'Asia/Kolkata'}).` : 'The monitors will keep evaluating and acting even when the market session is closed.'}</span>
            <small>This affects automatic market exits, GTT arming/rearming, and pending-order chase. Manual actions in the UI remain manual.</small>
          </div>
          <div className="config-section-title" style={{ marginTop: '14px' }}>
            <strong>Global GTT chase defaults</strong>
            <span>Used by Kite GTT plans unless a stock overrides them in its own Live execution panel.</span>
          </div>
          <div className="config-target-grid config-target-grid-wide">
            <label>
              SL limit buffer
              <div><input type="number" min="0" step="0.05" value={liveExecutionConfig.gtt_sl_limit_buffer_percent} onChange={(event) => updateLiveExecutionConfig({ gtt_sl_limit_buffer_percent: event.target.value })} /><span>%</span></div>
            </label>
            <label>
              Chase after
              <div><input type="number" min="0" step="500" value={liveExecutionConfig.gtt_order_chase_after_ms} onChange={(event) => updateLiveExecutionConfig({ gtt_order_chase_after_ms: event.target.value })} /><span>ms</span></div>
            </label>
            <label>
              Chase step
              <div><input type="number" min="0" step="0.05" value={liveExecutionConfig.gtt_order_chase_step_percent} onChange={(event) => updateLiveExecutionConfig({ gtt_order_chase_step_percent: event.target.value })} /><span>%</span></div>
            </label>
            <label>
              Max attempts
              <div><input type="number" min="0" step="1" value={liveExecutionConfig.gtt_order_chase_max_attempts} onChange={(event) => updateLiveExecutionConfig({ gtt_order_chase_max_attempts: event.target.value })} /></div>
            </label>
          </div>
          <div className="config-target-grid">
            <label>
              SL chase
              <div className="config-toggle">
                <input type="checkbox" checked={Boolean(liveExecutionConfig.gtt_order_chase_enabled)} onChange={(event) => updateLiveExecutionConfig({ gtt_order_chase_enabled: event.target.checked })} />
                <span>{liveExecutionConfig.gtt_order_chase_enabled ? 'Modify pending SL orders' : 'Do not chase SL orders'}</span>
              </div>
            </label>
            <label>
              Convert SL to market
              <div className="config-toggle">
                <input type="checkbox" checked={Boolean(liveExecutionConfig.gtt_order_chase_to_market)} onChange={(event) => updateLiveExecutionConfig({ gtt_order_chase_to_market: event.target.checked })} />
                <span>{liveExecutionConfig.gtt_order_chase_to_market ? 'Allowed after delay' : 'Keep as LIMIT'}</span>
              </div>
            </label>
            <label>
              Target chase
              <div className="config-toggle">
                <input type="checkbox" checked={Boolean(liveExecutionConfig.gtt_target_order_chase_enabled)} onChange={(event) => updateLiveExecutionConfig({ gtt_target_order_chase_enabled: event.target.checked })} />
                <span>{liveExecutionConfig.gtt_target_order_chase_enabled ? 'Also chase target exits' : 'SL only'}</span>
              </div>
            </label>
          </div>
          <div className="config-example">
            <strong>{liveExecutionConfig.gtt_order_chase_to_market ? 'Aggressive SL recovery' : 'Limit-order recovery'}</strong>
            <span>When a GTT SL order is pending after {Number(liveExecutionConfig.gtt_order_chase_after_ms) || 0} ms, the monitor will retry up to {Number(liveExecutionConfig.gtt_order_chase_max_attempts) || 0} time(s).</span>
            <small>GTT order legs remain LIMIT. These settings apply only after Kite triggers the GTT and creates a regular order.</small>
          </div>
          <div className="config-button-row">
            <button type="button" className="button button-primary" onClick={saveLiveExecutionConfig} disabled={liveExecutionLoading}>
              {liveExecutionLoading ? 'Saving...' : 'Save live execution alerts'}
            </button>
            <button type="button" className="button button-secondary" onClick={sendLiveExecutionTest} disabled={liveExecutionTesting}>
              {liveExecutionTesting ? 'Sending...' : 'Send live execution test'}
            </button>
            <button type="button" className="button button-secondary" onClick={saveLiveExecutionMonitorConfig} disabled={liveExecutionMonitorLoading}>
              {liveExecutionMonitorLoading ? 'Saving...' : 'Save live monitor frequency'}
            </button>
          </div>
          </div>
          {telegramTestResult ? <div className={`config-example telegram-test-result ${telegramTestResult.ok ? 'is-success' : 'is-error'}`}><strong>{telegramTestResult.ok ? '✓ Live execution notification sent' : '✕ Live execution notification failed'}</strong><span>{telegramTestResult.text}</span></div> : null}
        </div>

        <div style={{ display: activeConfigTab === 'symbols' ? 'block' : 'none' }}>
          <div className="config-section-title">
            <strong>NSE stock list update</strong>
            <span>Upload an NSE CSV with `SYMBOL` and `NAME OF COMPANY`. Duplicate symbols are removed automatically.</span>
          </div>
          <div className="config-example">
            <label className="button button-secondary" style={{ display: 'inline-flex', width: 'fit-content', cursor: 'pointer' }}>
              Choose symbol file
              <input type="file" onChange={selectSymbolFile} style={{ display: 'none' }} />
            </label>
            <button type="button" className="button button-primary" onClick={submitSymbolFile} disabled={symbolImportLoading || !symbolFile}>
              {symbolImportLoading ? 'Uploading...' : 'Submit CSV'}
            </button>
            {symbolFileName ? <small>Selected file: {symbolFileName}</small> : null}
            {symbolImportSummary ? (
              <small>
                {symbolImportSummary.new_symbols_added} new symbols added. {symbolImportSummary.unique_symbols} total unique symbols saved from {symbolImportSummary.imported_rows} imported rows.
                {symbolImportSummary.duplicates_removed ? ` Removed ${symbolImportSummary.duplicates_removed} duplicate rows.` : ''}
              </small>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
